import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'gatsby-plugin-react-i18next'
import cn from 'classnames'
import { navigate } from 'gatsby'
import { Spinner } from 'react-bootstrap'
import { v4 as uuidv4 } from 'uuid'
import { useWertWidget } from '@wert-io/module-react-component'
import { toast } from 'react-toastify'

import Modal from '~components/Modal'
import CardCTA from '~components/CardCTA'

import { fetchWertSession, getCountryFromCoords } from '~utils'
import useMatchMedia from '~hooks/useMatchMedia'
import creditCard from '~img/landing/creditCard.png'
import xcadWallet from '~img/landing/xcad-wallet.png'
import { centralizedExchanges, decentralizedExchanges } from './const'
import * as s from './Exchange.module.scss'

const RESTRICTED_COUNTRIES = ['argentina', 'india', 'peru']

const isRestrictedCountry = (country) => RESTRICTED_COUNTRIES.includes(country)

const Exchanges = () => {
  const { t } = useTranslation()
  const [tab, setTab] = useState('credit')
  const [openModal, setOpenModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const isMobile = useMatchMedia('(max-width: 850px)')
  const isWidgetOpenRef = useRef(false)
  const closeRef = useRef(() => {})

  const reactiveOptions = {
    theme: undefined,
    listeners: {
      position: (data) => console.info('step:', data.step),
      loaded: () => console.info('%c Widget loaded', 'color: green;'),
      'payment-status': (data) => {
        console.info('%c Payment status:', 'color: red;', data)
        if (data?.status === 'success') {
          if (isWidgetOpenRef.current) {
            closeRef.current()
          }
          setLoading(false)
          setOpenModal(false)
        }
      },
      close: () => {
        setLoading(false)
        isWidgetOpenRef.current = null
      },
      onsubmit: () => {
        setLoading(false)
        isWidgetOpenRef.current = null
      },
    },
  }

  const { open, close, isWidgetOpen } = useWertWidget(reactiveOptions)

  useEffect(() => {
    isWidgetOpenRef.current = isWidgetOpen
    closeRef.current = close
  }, [isWidgetOpen, close])

  useEffect(() => {
    if (isWidgetOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'auto'
    }

    return () => {
      document.body.style.overflow = 'auto'
    }
  }, [isWidgetOpen])

  const redirectToPayment = () => {
    const newOptions = {
      flow_type: 'simple',
      partner_id: process.env.GATSBY_WERT_PARTNER_ID,
      origin: process.env.GATSBY_WERT_ORIGIN,
      click_id: uuidv4(),
      currency: 'USD',
      commodity:
        process.env.GATSBY_VERCEL_ENV === 'production' ? 'XCAD' : 'BNB', // should be XCAD in production
      network: 'bsc',
    }

    if (navigator.geolocation) {
      setLoading(true)
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const country = await getCountryFromCoords(
              position.coords.latitude,
              position.coords.longitude
            )

            if (isRestrictedCountry(country)) {
              const fiat = country === 'argentina' ? '29' : '1' // 29 = ARS, 1 = INR
              navigate(
                `https://onramp.money/app/?appId=393788&fiatType=${fiat}&coinCode=xcad&network=bep20`
              )
            } else {
              const session = await fetchWertSession(newOptions)

              if (session && session.data && session.data.sessionId) {
                const sessionOptions = {
                  ...newOptions,
                  session_id: session.data.sessionId,
                }

                open({ options: sessionOptions })
              }
            }
          } catch (err) {
            console.error('Error in payment flow:', err)
            toast.error(t('Error in payment flow:', { err }))
          } finally {
            setLoading(false)
          }
        },
        (error) => {
          console.error('Geolocation error:', error)
          toast.error(t('Geolocation error:', { error }))
          setLoading(false)
        }
      )
    } else {
      open({ options: newOptions })
      console.error('Geolocation not supported.')
    }
  }

  return (
    <div className={cn(s.exchangesContainer, 'container')} id="how">
      {openModal && (
        <Modal
          onClose={() => setOpenModal(false)}
          modalChildren={
            <div className={s.sectionModal}>
              <div className={s.start}>{t('buyXCADNow')}</div>
              <div className={s.description}>
                <div>
                  <h3>{t('doYouHaveWallet')}</h3>
                </div>
                <img src={xcadWallet} alt="xcad wallet" />
              </div>
              <div className={s.btnContainer}>
                <button
                  type="button"
                  className={s.blackBtn}
                  onClick={redirectToPayment}
                  disabled={loading}
                >
                  {loading ? (
                    <Spinner animation="border" role="status" size="sm" />
                  ) : (
                    t('hasAccount')
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/register')}
                  disabled={loading}
                >
                  {t('noAccount')}
                </button>
              </div>
            </div>
          }
        />
      )}
      <div className={s.tabs}>
        <div
          role="button"
          tabIndex={0}
          onClick={() => setTab('credit')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              setTab('credit')
            }
          }}
          className={cn(s.tab, { [s.active]: tab === 'credit' })}
        >
          {t('creditCard')}
        </div>

        <div
          role="button"
          tabIndex={0}
          onClick={() => setTab('centralized')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              setTab('centralized')
            }
          }}
          className={cn(s.tab, { [s.active]: tab === 'centralized' })}
        >
          {isMobile ? 'CEX' : t('centralizedExchange')}
        </div>

        <div
          role="button"
          tabIndex={0}
          onClick={() => setTab('decentralized')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              setTab('decentralized')
            }
          }}
          className={cn(s.tab, { [s.active]: tab === 'decentralized' })}
        >
          {isMobile ? 'DEX' : t('decentralizedExchange')}
        </div>
      </div>

      <div className={s.exchanges}>
        {tab === 'credit' && (
          <div className={s.creditCardContainer}>
            <div className={s.text}>
              <h3>{t('buyXCAD')}</h3>
              <p>{t('buyXCADDescription')}</p>
              <strong>{t('createAccount')}</strong>

              <button
                type="button"
                id="buyweb"
                className={s.desktopButton}
                onClick={() => setOpenModal(true)}
              >
                {t('buyXCADNow')}
              </button>
            </div>

            <img src={creditCard} alt="credit card" />

            <button
              type="button"
              id="buyweb"
              className={s.mobileButton}
              onClick={() => setOpenModal(true)}
            >
              {t('buyXCADNow')}
            </button>
          </div>
        )}

        {tab === 'centralized' && (
          <div className={s.exchangeContainer}>
            <h3>{t('listCentralized')}</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              {centralizedExchanges.map((exchange) => (
                <CardCTA
                  title={exchange.name}
                  icon={exchange.icon}
                  url={exchange.url}
                  featured={exchange.featured}
                />
              ))}
            </div>
          </div>
        )}

        {tab === 'decentralized' && (
          <div className={s.exchangeContainer}>
            <h3>{t('listDecentralized')}</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              {decentralizedExchanges.map((exchange) => (
                <CardCTA
                  title={exchange.name}
                  icon={exchange.icon}
                  url={exchange.url}
                  featured={exchange.featured}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Exchanges
